import { Icon } from "../../../components/ui/Icon";
import { Toolbar } from "../../../components/ui/Toolbar";
import { DragHandle } from "@knowt/editor/extensions/dragHandle";
import { Editor } from "@knowt/editor/react";
import { Popover } from "../../ui/Popover";
import { Surface } from "../../../components/ui/Surface";
import { DropdownButton } from "../../../components/ui/Dropdown";
import useContentItemActions from "./hooks/useContentItemActions";
import { useData } from "./hooks/useData";
import { useThemeContextSelector } from "@/utils/theme";
import { FlexRowAlignCenter } from "@/components/Flex";
import { useCallback } from "react";

export type ContentItemMenuProps = {
    editor: Editor;
};

export const ContentItemMenu = ({ editor }: ContentItemMenuProps) => {
    const isDarkMode = useThemeContextSelector(state => state.isDarkMode);
    const data = useData();
    const actions = useContentItemActions(editor, data.currentNode, data.currentNodePos);

    const handleMenuIsOpen = useCallback(() => {
        editor.commands.setMeta("lockDragHandle", true);
    }, [editor.commands]);

    const handleMenuIsClose = useCallback(() => {
        editor.commands.setMeta("lockDragHandle", false);
    }, [editor.commands]);

    const handleDragStart = useCallback(() => {
        editor.commands.setDragHandleActive(true);
    }, [editor.commands]);

    const handleDragEnd = useCallback(() => {
        editor.commands.setDragHandleActive(false);
        setTimeout(() => editor.commands.setTextSelection(0));
    }, [editor.commands]);

    return (
        <DragHandle
            pluginKey="ContentItemMenu"
            editor={editor}
            onNodeChange={data.handleNodeChange}
            tippyOptions={{
                offset: [-5, 16],
                zIndex: 1,
            }}>
            <FlexRowAlignCenter style={{ gap: "2px" }}>
                <Toolbar.Button onMouseDownCapture={actions.handleAdd}>
                    <Icon name="Plus" />
                </Toolbar.Button>
                <Popover
                    onOpen={handleMenuIsOpen}
                    onClose={handleMenuIsClose}
                    PopupProps={{ placement: "bottom-start" }}
                    content={({ closePopup }) => (
                        <Surface
                            style={{ padding: "8px", display: "flex", flexDirection: "column", minWidth: "256px" }}
                            onClick={() => {
                                closePopup();
                            }}>
                            <DropdownButton onClick={() => actions.resetTextFormatting()}>
                                <Icon name="RemoveFormatting" />
                                Clear formatting
                            </DropdownButton>
                            <DropdownButton onClick={() => actions.copyNodeToClipboard()}>
                                <Icon name="Clipboard" />
                                Copy to clipboard
                            </DropdownButton>
                            <DropdownButton onClick={() => actions.duplicateNode()}>
                                <Icon name="Copy" />
                                Duplicate
                            </DropdownButton>
                            <Toolbar.Divider horizontal />
                            <DropdownButton
                                onClick={() => actions.deleteNode()}
                                sx={{
                                    color: "#EF4444",
                                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                                    "&:hover": {
                                        backgroundColor: isDarkMode
                                            ? "rgba(239, 68, 68, 0.2)"
                                            : "rgba(239, 68, 68, 0.2)",
                                        color: isDarkMode ? "#EF4444" : "inherit",
                                    },
                                }}>
                                <Icon name="Trash2" />
                                Delete
                            </DropdownButton>
                        </Surface>
                    )}>
                    {({ openPopup }) => (
                        <Toolbar.Button
                            draggable
                            onMouseDown={handleDragStart}
                            onMouseUp={handleDragEnd}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            onClick={e => {
                                openPopup(e);
                            }}>
                            <Icon name="GripVertical" />
                        </Toolbar.Button>
                    )}
                </Popover>
            </FlexRowAlignCenter>
        </DragHandle>
    );
};
